import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, catchError, of, switchMap, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CategorizationSummary,
  ExportStartOptions,
  ExportState,
  SlaSummary,
  SmilebackReviewsPage,
  SmilebackSummary,
  TicketAnalysisFilters,
  TicketAnalysisFiltersResponse,
  TicketAnalysisPage,
  VarianceSummary,
} from '../models/ticket-analysis.models';

@Injectable({
  providedIn: 'root',
})
export class TicketAnalysisService {
  private readonly baseUrl = `${environment.restUrl}/v1/msp/ticket-analysis`;
  private readonly EXPORT_KEY = 'ticket_analysis_export_instance';
  private pollingSub?: Subscription;

  readonly exportRequested$ = new Subject<void>();
  readonly exportState$ = new BehaviorSubject<ExportState>({ status: 'idle' });
  readonly downloadReady$ = new Subject<{ sasUrl: string; filename: string }>();

  constructor(private http: HttpClient) {
    this.resumeIfPending();
  }

  getCategorization(filters: TicketAnalysisFilters): Observable<CategorizationSummary> {
    return this.http.get<CategorizationSummary>(`${this.baseUrl}/categorization`, {
      params: this.toParams(filters),
    });
  }

  getTickets(
    filters: TicketAnalysisFilters,
    page: number,
    pageSize: number,
    sortBy = 'dateEntered',
    sortDir: 'asc' | 'desc' = 'desc',
  ): Observable<TicketAnalysisPage> {
    return this.http.get<TicketAnalysisPage>(`${this.baseUrl}/tickets`, {
      params: { ...this.toParams(filters), page, pageSize, sortBy, sortDir },
    });
  }

  getFilters(): Observable<TicketAnalysisFiltersResponse> {
    return this.http.get<TicketAnalysisFiltersResponse>(`${this.baseUrl}/filters`);
  }

  getVariance(filters: TicketAnalysisFilters): Observable<VarianceSummary> {
    return this.http.get<VarianceSummary>(`${this.baseUrl}/variance`, {
      params: this.toParams(filters),
    });
  }

  getSla(filters: TicketAnalysisFilters): Observable<SlaSummary> {
    return this.http.get<SlaSummary>(`${this.baseUrl}/sla`, {
      params: this.toParams(filters),
    });
  }

  getTicketsForSla(
    filters: TicketAnalysisFilters,
    priority: string,
    page: number,
    pageSize: number,
    sortBy = 'dateEntered',
    sortDir: 'asc' | 'desc' = 'desc',
  ): Observable<TicketAnalysisPage> {
    return this.http.get<TicketAnalysisPage>(`${this.baseUrl}/tickets`, {
      params: {
        ...this.toParams({ ...filters, filterByPriority: priority, includeSla: 'true' }),
        page,
        pageSize,
        sortBy,
        sortDir,
      },
    });
  }

  startExport(filters: TicketAnalysisFilters, options: ExportStartOptions): void {
    if (this.exportState$.value.status === 'pending') return;

    const params: Record<string, string> = { ...this.toParams(filters), view: options.view };
    if (options.includeSla) params['includeSla'] = 'true';
    if (options.filterByType) params['filterByType'] = options.filterByType;
    if (options.filterBySubType) params['filterBySubType'] = options.filterBySubType;
    if (options.filterByItem) params['filterByItem'] = options.filterByItem;

    this.exportState$.next({ status: 'pending' });
    this.http.post<{ instanceId: string }>(`${this.baseUrl}/export/start`, null, { params }).subscribe({
      next: ({ instanceId }) => {
        sessionStorage.setItem(this.EXPORT_KEY, instanceId);
        this.exportState$.next({ status: 'pending', instanceId });
        this.startPolling(instanceId);
      },
      error: () => this.exportState$.next({ status: 'failed' }),
    });
  }

  getSmileback(filters: TicketAnalysisFilters): Observable<SmilebackSummary> {
    return this.http.get<SmilebackSummary>(`${this.baseUrl}/smileback`, {
      params: this.toParams(filters),
    });
  }

  getSmilebackReviews(
    filters: TicketAnalysisFilters,
    page: number,
    pageSize: number,
    sortBy = 'dateRated',
    sortDir: 'asc' | 'desc' = 'desc',
  ): Observable<SmilebackReviewsPage> {
    return this.http.get<SmilebackReviewsPage>(`${this.baseUrl}/smileback/reviews`, {
      params: { ...this.toParams(filters), page, pageSize, sortBy, sortDir },
    });
  }

  getExportDownload(instanceId: string): Observable<{ sasUrl: string; filename: string }> {
    return this.http.get<{ sasUrl: string; filename: string }>(`${this.baseUrl}/export/download`, {
      params: { instanceId },
    });
  }

  resetExportState(): void {
    this.pollingSub?.unsubscribe();
    sessionStorage.removeItem(this.EXPORT_KEY);
    this.exportState$.next({ status: 'idle' });
  }

  private resumeIfPending(): void {
    const instanceId = sessionStorage.getItem(this.EXPORT_KEY);
    if (instanceId) {
      this.exportState$.next({ status: 'pending', instanceId });
      this.startPolling(instanceId);
    }
  }

  private startPolling(instanceId: string): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = timer(2000, 3000)
      .pipe(
        switchMap(() =>
          this.http
            .get<{ status: string; filename?: string }>(`${this.baseUrl}/export/status`, {
              params: { instanceId },
            })
            .pipe(
              catchError((err) =>
                of(
                  err?.status === 404
                    ? ({ status: 'failed', filename: undefined } as { status: string; filename?: string })
                    : null,
                ),
              ),
            ),
        ),
      )
      .subscribe({
        next: (result) => {
          if (!result) return;
          if (result.status === 'completed') {
            this.pollingSub?.unsubscribe();
            sessionStorage.removeItem(this.EXPORT_KEY);
            this.exportState$.next({ status: 'completed', instanceId, filename: result.filename });
            this.getExportDownload(instanceId).subscribe({
              next: ({ sasUrl, filename }) => this.downloadReady$.next({ sasUrl, filename }),
            });
          } else if (result.status === 'failed') {
            this.pollingSub?.unsubscribe();
            sessionStorage.removeItem(this.EXPORT_KEY);
            this.exportState$.next({ status: 'failed' });
          }
        },
      });
  }

  private toParams(filters: TicketAnalysisFilters): Record<string, string> {
    return Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== '')) as Record<
      string,
      string
    >;
  }
}
